import { useRef, type FocusEvent } from 'react';

import { HistogramInspect } from './HistogramExplore/HistogramInspect.js';
import { useApplyPanDelta } from './hooks/useApplyPanDelta.js';
import { useApplyZoomDelta } from './hooks/useApplyZoomDelta.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { InteractionsOverlay } from '../../../core/components/interactions-overlay/InteractionsOverlay.js';
import {
  INTERACTION_MODE,
  type InteractionMode,
  type InteractionTriggers,
  isSelectCancelled,
  type ZoomBehavior,
} from '../../../core/components/interactions-overlay/types.js';
import { isOwnEvent } from '../../../core/components/interactions-overlay/utils/is-own-event.js';
import {
  reduceExploreCancelAll,
  reduceExploreCancelSelect,
  reduceExploreCancelTooltip,
  reduceExploreEnd,
  reduceExplorePin,
  reduceExplorePosition,
  reduceExploreReset,
  reduceExploreSelect,
  reduceExploreUnPin,
} from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import { reduceInteractionMode } from '../../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { useToolbarHandlerRef } from '../../../core/components/toolbar/hooks/useToolbarHandlerRef.js';
import type { RelativeAbsolutePosition } from '../../../core/types/pointer-events.js';
import { getPercentageFromPixel } from '../../../core/utils/get-percentage-from-pixel.js';
import { HistogramChartOverlayRefContext } from '../../contexts/HistogramChartOverlayRefContext.js';
import { HistogramSelectionAreaRef } from '../../contexts/HistogramSelectionAreaRef.context.js';
import {
  useExploreState,
  useInteractionModeState,
  useSelectState,
  useZoomState,
} from '../../hooks/store/selectors.js';
import { useSetState } from '../../hooks/store/state-setters.js';
import { useHistogramInteractions } from '../../hooks/useHistogramInteractions.js';
import { useHistogramToolbar } from '../../hooks/useHistogramToolbar.js';
import { useResetInteractions } from '../../hooks/useResetInteractions.js';
import { useResetOnResize } from '../../hooks/useResetOnResize.js';
import { useZoomToSelection } from '../../hooks/useZoomToSelection.js';
import { HistogramToolbar } from '../toolbar/HistogramToolbar.js';
import { HistogramCrosshair } from './HistogramExplore/HistogramCrosshair.js';
import { HistogramSelectionArea } from './HistogramSelectionArea/HistogramSelectionArea.js';
import type {
  _SelectionRef,
  SelectionAreaVariant,
} from '../../../core/components/selection-area/types.js';
import { useSetClosestBins } from '../../hooks/useSetClosestBins.js';

export const HistogramOverlays = () => {
  const handlerRef = useToolbarHandlerRef();
  /* eslint-disable-next-line @typescript-eslint/no-deprecated */
  const { zoomX, pan, zoom } = useHistogramInteractions();
  const selectionRef = useRef<_SelectionRef>(null);

  const { width, height } = useGridPlotSize();
  const toolbar = useHistogramToolbar();
  const setState = useSetState();
  const mode = useInteractionModeState();
  const exploreState = useExploreState();
  const selectState = useSelectState();
  const zoomState = useZoomState();
  const overlayRef = useRef<HTMLDivElement>(null);

  const applyPanDelta = useApplyPanDelta();
  const applyZoomDelta = useApplyZoomDelta();
  const zoomToSelection = useZoomToSelection();
  const resetInteractions = useResetInteractions();
  const setClosestBins = useSetClosestBins();

  useResetOnResize();

  const handleModeChange = (event: {
    mode: InteractionMode;
    keyboard: boolean;
  }) => {
    const canPan = zoomState.zoomOutEnabled;
    const shouldSwitchToPanMode =
      event.mode === 'pan' && pan?.enabled && canPan;
    const shouldSwitchToZoomMode =
      event.mode === 'zoom' && (zoomX?.enabled || zoom?.enabled);

    if (shouldSwitchToPanMode || shouldSwitchToZoomMode) {
      setState(reduceInteractionMode(event.mode));
      setState(reduceExploreReset);
    } else if (event.mode === 'explore') {
      setState(reduceInteractionMode(INTERACTION_MODE.Explore));
    }
  };

  const handleFocus = (event?: FocusEvent) => {
    if (!event || event.cancelable) {
      return;
    }
    if (isOwnEvent(event)) {
      handlerRef.current.handleFocus();
    }
  };

  const handleHover = (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => {
    if (mode === 'explore') {
      setClosestBins(event.position);

      setState(reduceExplorePosition(event.position));
    }
  };

  const handleLeave = (_: { triggeredBy: InteractionTriggers }) => {
    if (mode === 'explore') {
      setState(reduceExploreEnd);
    }
  };

  const handlePanChange = (event: {
    triggeredBy: InteractionTriggers;
    delta: number;
  }) => {
    if (pan?.enabled) {
      applyPanDelta(event.delta);
    }
  };

  const handleTogglePin = (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition | undefined;
    pin: boolean;
  }) => {
    if (event.pin) {
      const highlightedSeriesId = setClosestBins(event.position);

      setState(reduceExplorePin(event.position, highlightedSeriesId));
    } else {
      setState(reduceExploreUnPin(event.position));
    }
  };

  const handleReset = (event: {
    triggeredBy: InteractionTriggers;
    mousePosition?: RelativeAbsolutePosition;
  }) => {
    resetInteractions(event.mousePosition);
  };

  const handleSelectStart = (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => {
    if (!zoomX?.enabled) {
      handleTogglePin({
        triggeredBy: event.triggeredBy,
        position: event.position,
        pin: !exploreState.pinned,
      });
    }
  };

  const handleSelectChange = (event: {
    triggeredBy: InteractionTriggers;
    start: RelativeAbsolutePosition;
    end: RelativeAbsolutePosition;
    mode: 'zoom' | 'explore';
  }): void => {
    if (zoomX?.enabled || mode === 'explore') {
      const variant: SelectionAreaVariant =
        event.mode === 'explore' ? 'select' : 'zoom';

      setState(
        reduceExploreSelect({
          start: event.start,
          end: event.end,
          finished: false,
          accessible: false,
          variant,
          selectionPercentage: getPercentageFromPixel(
            [event.start, event.end],
            width,
          ),
        }),
      );
    }
  };

  const handleSelectEnd = (
    event:
      | {
          triggeredBy: InteractionTriggers;
          start: RelativeAbsolutePosition;
          end: RelativeAbsolutePosition;
        }
      | { cancelled: true },
  ): void => {
    if (isSelectCancelled(event)) {
      setState(reduceExploreCancelSelect);
    } else if (mode === 'explore') {
      setState(
        reduceExploreSelect({
          start: event.start,
          end: event.end,
          finished: true,
          accessible: false,
          selectionPercentage: getPercentageFromPixel(
            [event.start, event.end],
            width,
          ),
        }),
      );
      selectionRef.current?.focus();
    } else if (zoomX?.enabled) {
      // in case select is disabled and zoomX is enabled, only zoom to selection interaction is possible
      zoomToSelection();
    }
  };

  const handleZoomChange = (event: {
    triggeredBy: InteractionTriggers;
    deltaStart: number;
    deltaEnd: number;
    behavior: ZoomBehavior;
  }) => {
    applyZoomDelta(event.deltaStart, event.deltaEnd, event.behavior);
    setState(reduceExploreCancelAll);
  };

  const handleToolbarMouseEnter = () => {
    setState(reduceExploreCancelTooltip);
  };

  return (
    <InteractionsOverlay
      ref={overlayRef}
      mode={mode}
      pinned={exploreState.pinned}
      selectionArea={
        selectState ? [selectState?.start, selectState?.end] : undefined
      }
      width={width}
      height={height}
      isZoomDisabled={!zoom?.enabled}
      isZoomXDisabled={!zoomX?.enabled}
      isPanDisabled={!(pan?.enabled && zoomState.zoomOutEnabled)}
      onModeChange={handleModeChange}
      onFocus={handleFocus}
      onHover={handleHover}
      onLeave={handleLeave}
      onPanChange={handlePanChange}
      onTogglePin={handleTogglePin}
      onReset={handleReset}
      onSelectStart={handleSelectStart}
      onSelectChange={handleSelectChange}
      onSelectEnd={handleSelectEnd}
      onZoomChange={handleZoomChange}
      data-testid="histogram-chart-overlay"
      style={{ userSelect: selectState?.finished === false ? 'none' : 'auto' }}
    >
      <HistogramChartOverlayRefContext.Provider value={overlayRef}>
        {exploreState.visible ? (
          <>
            <HistogramCrosshair />
            <HistogramInspect />
          </>
        ) : null}
        <HistogramSelectionAreaRef.Provider value={selectionRef}>
          {toolbar.enabled ? (
            <HistogramToolbar
              onMouseEnter={handleToolbarMouseEnter}
              handlerRef={handlerRef}
            />
          ) : null}
          <HistogramSelectionArea />
        </HistogramSelectionAreaRef.Provider>
      </HistogramChartOverlayRefContext.Provider>
    </InteractionsOverlay>
  );
};
